import { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost, ApiError } from '../../lib/api';
import { useAsync } from '../../lib/useAsync';
import { ToolGate } from '../../components/ToolGate';
import { Button, Field, Input, Alert, Loading, EmptyState, Badge } from '../../components/ui';
import { tierColor } from '../../lib/types';
import { ResearchButton } from '../../components/DeepResearch';
import { ContactControls, ContactFilters, DEFAULT_FILTERS, filtersToQuery } from '../../components/ContactControls';
import { WorkStatus } from '../../components/WorkStatus';

interface QueueContact {
  id: number;
  businessName: string | null;
  contactName: string | null;
  phone: string;
  tier: string | null;
  score: number | null;
  category: string | null;
  callStatus: string | null;
  callAttempts: number;
  lastCallAt: string | null;
  stageId: string | null;
  stageName: string | null;
  researched?: boolean;
  workStatus?: string;
}

const CALLER_SORTS = [
  { v: 'priority', label: 'Best to call first' },
  { v: 'score', label: 'Score (high→low)' },
  { v: 'name', label: 'Name (A→Z)' },
  { v: 'added', label: 'Newest added' },
];

interface Stage {
  id: string;
  name: string;
  isWon: number;
  isLost: number;
}

const QUICK_OUTCOMES = ['No answer', 'Left voicemail', 'Bad number', 'Connected'];

export default function Caller() {
  const [filters, setFilters] = useState<ContactFilters>({ ...DEFAULT_FILTERS, sort: 'priority' });
  const queue = useAsync<{ queue: QueueContact[]; categories: string[] }>(
    () => apiGet(`/api/tools/caller?${filtersToQuery(filters)}`),
    [filters]
  );
  const stages = useAsync<{ stages: Stage[] }>(
    () => apiGet('/api/stages'),
    []
  );

  const [activeId, setActiveId] = useState<number | null>(null);
  const [outcome, setOutcome] = useState<string>('');
  const [customOutcome, setCustomOutcome] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [stageId, setStageId] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  function openRow(id: number) {
    if (activeId === id) {
      setActiveId(null);
    } else {
      setActiveId(id);
      setOutcome('');
      setCustomOutcome('');
      setNotes('');
      setStageId('');
      setSaveError(null);
    }
  }

  async function saveCall() {
    if (!activeId) return;
    const finalOutcome = outcome === 'custom' ? customOutcome.trim() : outcome;
    if (!finalOutcome) {
      setSaveError('Please select or enter an outcome.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await apiPost('/api/tools/caller', {
        contactId: activeId,
        outcome: finalOutcome,
        notes: notes.trim() || undefined,
        stageId: stageId || undefined,
      });
      setActiveId(null);
      setOutcome('');
      setCustomOutcome('');
      setNotes('');
      setStageId('');
      queue.reload();
    } catch (e) {
      setSaveError(e instanceof ApiError ? e.message : 'Failed to save call.');
    } finally {
      setSaving(false);
    }
  }

  const stageList = stages.data?.stages ?? [];
  const contacts = queue.data?.queue ?? [];

  return (
    <ToolGate tool="caller" title="Calls">
      <div className="mx-auto max-w-3xl px-4 py-8">
        {/* Heading */}
        <div className="mb-6 flex items-start justify-between gap-3">
          <div>
            <h1 className="h-display text-3xl text-soft">Calls</h1>
            <p className="mt-1 text-sm text-muted">
              Work your list. Every call logs itself and moves your pipeline.
            </p>
          </div>
          <Link to="/contacts?import=1" className="shrink-0 text-sm font-semibold text-electric hover:underline">Import CSV →</Link>
        </div>

        {/* Sort / filter — shared with Contacts + Lead Score */}
        <div className="mb-4">
          <ContactControls value={filters} onChange={setFilters} categories={queue.data?.categories || []} sorts={CALLER_SORTS} />
        </div>

        {/* Queue error */}
        {queue.error && (
          <div className="mb-4">
            <Alert kind="error">{queue.error}</Alert>
          </div>
        )}

        {/* Loading */}
        {queue.loading && <Loading label="Loading call queue…" />}

        {/* Empty */}
        {!queue.loading && !queue.error && contacts.length === 0 && (
          <EmptyState
            title="No one to call yet"
            body="Add contacts with phone numbers or source some leads."
          />
        )}

        {/* Queue list */}
        {!queue.loading && contacts.length > 0 && (
          <div className="flex flex-col gap-3">
            {contacts.map((c) => {
              const isActive = activeId === c.id;
              const displayName = c.businessName || c.contactName || 'Unknown';
              const subName = c.businessName && c.contactName ? c.contactName : null;
              const attemptLabel =
                c.callAttempts === 0
                  ? 'Never called'
                  : `${c.callAttempts} attempt${c.callAttempts === 1 ? '' : 's'}`;
              const lastCallLabel = c.lastCallAt
                ? new Date(c.lastCallAt).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                  })
                : null;

              return (
                <div key={c.id} className="card overflow-hidden">
                  {/* Row header */}
                  <button
                    className="flex w-full items-center gap-3 px-4 py-3.5 text-left hover:bg-white/5 transition-colors"
                    onClick={() => openRow(c.id)}
                    aria-expanded={isActive}
                  >
                    {/* Tier badge */}
                    <Badge className={`shrink-0 border-current ${tierColor(c.tier)}`}>
                      {c.tier ?? '—'}
                    </Badge>

                    {/* Names */}
                    <div className="min-w-0 flex-1">
                      <span className="block truncate font-semibold text-soft">{displayName}</span>
                      {subName && (
                        <span className="block truncate text-xs text-muted">{subName}</span>
                      )}
                    </div>

                    {/* Phone */}
                    <a
                      href={`tel:${c.phone}`}
                      className="shrink-0 text-sm text-electric hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {c.phone}
                    </a>

                    {c.researched && <span className="shrink-0 text-xs text-electric" title="Deep Research done">🔎</span>}
                    <span className="shrink-0" onClick={(e) => e.stopPropagation()}><WorkStatus contactId={c.id} value={c.workStatus || 'new'} /></span>

                    {/* Call history */}
                    <div className="hidden shrink-0 text-right text-xs text-muted sm:block">
                      <span>{attemptLabel}</span>
                      {lastCallLabel && (
                        <span className="block">{lastCallLabel}</span>
                      )}
                    </div>

                    {/* Deep Research (shared endpoint) + contact link */}
                    <ResearchButton contactId={c.id} className="shrink-0" />
                    <Link
                      to={`/contacts/${c.id}`}
                      className="shrink-0 text-xs text-muted hover:text-soft"
                      onClick={(e) => e.stopPropagation()}
                    >
                      View →
                    </Link>

                    {/* Expand indicator */}
                    <span className="shrink-0 text-muted" aria-hidden>
                      {isActive ? '▲' : '▼'}
                    </span>
                  </button>

                  {/* Inline log panel */}
                  {isActive && (
                    <div className="border-t border-line px-4 py-4">
                      {saveError && (
                        <div className="mb-3">
                          <Alert kind="error">{saveError}</Alert>
                        </div>
                      )}

                      {/* Quick-outcome buttons */}
                      <div className="mb-3">
                        <span className="mb-2 block text-xs font-semibold text-muted uppercase tracking-wide">
                          Outcome
                        </span>
                        <div className="flex flex-wrap gap-2">
                          {QUICK_OUTCOMES.map((o) => (
                            <button
                              key={o}
                              onClick={() => setOutcome(o)}
                              className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                                outcome === o
                                  ? 'border-electric bg-electric/10 text-electric'
                                  : 'border-line text-muted hover:border-electric/50 hover:text-soft'
                              }`}
                            >
                              {o}
                            </button>
                          ))}
                          <button
                            onClick={() => setOutcome('custom')}
                            className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                              outcome === 'custom'
                                ? 'border-electric bg-electric/10 text-electric'
                                : 'border-line text-muted hover:border-electric/50 hover:text-soft'
                            }`}
                          >
                            Other…
                          </button>
                        </div>
                      </div>

                      {/* Custom outcome input */}
                      {outcome === 'custom' && (
                        <div className="mb-3">
                          <Field label="Custom outcome">
                            <Input
                              value={customOutcome}
                              onChange={(e) => setCustomOutcome(e.target.value)}
                              placeholder="Describe what happened"
                            />
                          </Field>
                        </div>
                      )}

                      {/* Notes */}
                      <div className="mb-3">
                        <Field label="Notes (optional)">
                          <Input
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Anything worth remembering…"
                          />
                        </Field>
                      </div>

                      {/* Stage dropdown */}
                      {stageList.length > 0 && (
                        <div className="mb-4">
                          <Field label="Move to stage (optional)">
                            <select
                              className="input"
                              value={stageId}
                              onChange={(e) => setStageId(e.target.value)}
                            >
                              <option value="">— keep current stage —</option>
                              {stageList.map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.name}
                                </option>
                              ))}
                            </select>
                          </Field>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <Button loading={saving} onClick={saveCall}>
                          Save call
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => setActiveId(null)}
                          disabled={saving}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </ToolGate>
  );
}
